import { ArrowLeft, Cookie, Shield } from 'lucide-react'

/**
 * In-app policy pages backing the cookie-consent banner's links.
 * They summarize docs/en/10-privacy-policy.md and 11-cookie-policy.md —
 * the docs pack is the source of truth for maintainers.
 */

function PolicyLayout({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <a href="/" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to the app
        </a>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-50">
              {icon}
            </div>
            <h1 className="text-xl font-bold text-gray-900">{title}</h1>
          </div>
          <div className="space-y-4">{children}</div>
        </div>
      </div>
    </div>
  )
}

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">{heading}</h2>
      <div className="text-sm text-gray-600 leading-relaxed space-y-1.5">{children}</div>
    </section>
  )
}

export function PrivacyPage() {
  return (
    <PolicyLayout icon={<Shield className="w-5 h-5 text-blue-600" />} title="Privacy Policy">
      <Section heading="What we collect">
        <p>Inventory records, user accounts, and session tokens — stored in the instance's database. Uploaded item images are stored in object storage. No analytics, no browser tracking, and no personal data beyond a username.</p>
      </Section>
      <Section heading="Where it is stored">
        <p>All data lives in the operator's database and object storage (Cloudflare D1 and Tigris) — and nowhere else. The public repository and demo contain sample data only.</p>
      </Section>
      <Section heading="How long we keep it">
        <p>Inventory is kept for a default 2 years from last update; accounts until you or an admin deletes one; session tokens expire automatically; audit and import data is kept for 1 year.</p>
      </Section>
      <Section heading="Your rights">
        <p>You can access your account, correct your data, delete your account (other roles: with admin approval), and export everything you can see as CSV.</p>
      </Section>
      <Section heading="Sharing">
        <p>None. All processing happens on the instance's own servers. Password-breach checking uses a one-way prefix, never your password.</p>
      </Section>
    </PolicyLayout>
  )
}

export function CookiePage() {
  return (
    <PolicyLayout icon={<Cookie className="w-5 h-5 text-blue-600" />} title="Cookie Policy">
      <Section heading="Our cookies">
        <table className="w-full text-left text-sm border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-50 text-gray-900">
            <tr>
              <th className="px-3 py-2 font-semibold border-b border-gray-200">Cookie</th>
              <th className="px-3 py-2 font-semibold border-b border-gray-200">Purpose</th>
              <th className="px-3 py-2 font-semibold border-b border-gray-200">Lifetime</th>
            </tr>
          </thead>
          <tbody className="text-gray-600">
            <tr>
              <td className="px-3 py-2 border-b border-gray-100 font-mono text-xs">auth_token</td>
              <td className="px-3 py-2 border-b border-gray-100">Keeps you signed in (access token)</td>
              <td className="px-3 py-2 border-b border-gray-100">8 hours, auto-expires</td>
            </tr>
            <tr>
              <td className="px-3 py-2 font-mono text-xs">refresh_token</td>
              <td className="px-3 py-2">Quietly renews your session; revocable on logout</td>
              <td className="px-3 py-2">7 days</td>
            </tr>
          </tbody>
        </table>
        <p>Both are first-party, <strong>httpOnly</strong> (unreadable by page scripts), and sent only over HTTPS.</p>
      </Section>
      <Section heading="What we don't use">
        <p>No third-party cookies. No tracking, advertising, or analytics cookies. The only first-party flag is the one this banner's OK button sets, so it isn't shown again.</p>
      </Section>
    </PolicyLayout>
  )
}
